import { useCallback } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Texts of Sayfa Yetkileri (`pageAccess.*`). Menu titles stay Turkish in the
 * data and are translated by path (tMenu), as in the sidebar. Stored roles
 * ('admin', 'Kısıtlı Admin', …) are shown as they are, except 'employee'.
 */
const useAccessText = () => {
  const { t, tMenu, language, locale } = useTranslation();
  const label = useCallback((key, vars) => t(`pageAccess.${key}`, vars), [t]);

  const pageTitle = useCallback((page) => tMenu(page.path, page.title), [tMenu]);
  const groupTitle = useCallback((groupPath, group) => (groupPath ? tMenu(groupPath, group) : group), [tMenu]);
  const roleLabel = useCallback((role) => (role === 'employee' ? t('pageAccess.roles.employee') : role), [t]);

  /** Title / group of the page an online user is on (from describePath). */
  const placeLabel = useCallback((place) => {
    if (!place) return null;
    if (place.special === 'hall') return { group: 'Hall', title: t('shell.hall') };
    if (place.special === 'login') return { group: t('pageAccess.activity.loginGroup'), title: t('pageAccess.activity.login') };
    if (place.special === 'other') return { group: t('pageAccess.activity.other'), title: place.title };
    return { group: place.groupPath ? tMenu(place.groupPath, place.group) : place.group, title: tMenu(place.path, place.title) };
  }, [t, tMenu]);

  // "4 dk", "1 sa 12 dk" gibi kısa süre etiketi (çevrimiçi listesi)
  const formatSince = useCallback((ms) => {
    if (!Number.isFinite(ms) || ms < 0) return null;
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return t('pageAccess.time.justNow');
    if (minutes < 60) return t('pageAccess.time.minutes', { m: minutes });
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest > 0 ? t('pageAccess.time.hoursMinutes', { h: hours, m: rest }) : t('pageAccess.time.hours', { h: hours });
  }, [t]);

  // Kullanım istatistiklerindeki süre (boşsa "—")
  const formatDuration = useCallback((ms) => {
    if (!ms || ms <= 0) return '—';
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return t('pageAccess.time.hoursMinutes', { h, m });
    if (m > 0) return t('pageAccess.time.minutes', { m });
    return t('pageAccess.time.seconds', { s: Math.max(1, Math.floor(ms / 1000)) });
  }, [t]);

  const formatRelative = useCallback((ms) => {
    if (!ms) return t('pageAccess.time.never');
    const diff = Date.now() - ms;
    if (diff < 60000) return t('pageAccess.time.justNowCap');
    if (diff < 3600000) return t('pageAccess.time.minutesAgo', { m: Math.floor(diff / 60000) });
    if (diff < DAY_MS) return t('pageAccess.time.hoursAgo', { h: Math.floor(diff / 3600000) });
    return t('pageAccess.time.daysAgo', { d: Math.floor(diff / DAY_MS) });
  }, [t]);

  const formatDateTime = useCallback((ms) => (ms
    ? new Date(ms).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'), [locale]);

  const parseDevice = useCallback((ua) => {
    if (!ua) return { label: t('pageAccess.stats.unknownDevice'), isMobile: false };
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    let browser = t('pageAccess.stats.browser');
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';
    let os = '';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/iPhone|iPad/.test(ua)) os = 'iOS';
    else if (/Mac OS/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/Linux/.test(ua)) os = 'Linux';
    return { label: os ? `${browser} · ${os}` : browser, isMobile };
  }, [t]);

  return {
    t, language, locale, label, pageTitle, groupTitle, roleLabel, placeLabel,
    formatSince, formatDuration, formatRelative, formatDateTime, parseDevice,
  };
};

export default useAccessText;
